/**
 * @file OfficeSummaryModal.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-13
 * @description "Send Transport Summary to Office" — who gets the day's summary
 *              PDF. Shared by the bulk and bag boards (it was a copy in each).
 *
 *              Defaults: the office address AND every active sales rep with an
 *              email, each ticked unless the server says not (H J Ward);
 *              the user can untick any rep, add themselves
 *              or type other addresses. Reps come from orders.sales_reps
 *              (GET /api/transport/planning/office-summary-recipients).
 *
 *              An optional notice (EmailNoticeField) goes at the top of the
 *              email, under the greeting: onSend(recipients, { notice }).
 *
 *              Mount it only while open: useSlideOutButton must run in the
 *              render that adds the footer buttons.
 *
 *              1.3.0 (2026-10-12): `warning` — a sentence at the top that does
 *              not stop the send (self-billed POs not planned yet —
 *              utils/selfBilledPo).
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import { useSlideOutButton } from './SlideOutButton';
import EmailNoticeField from './EmailNoticeField';
import SenderSelect from './SenderSelect';
import SendWindowWarning from './SendWindowWarning';
import './OfficeSummaryModal.css';

// Until the server says otherwise (TRANSPORT_OFFICE_EMAIL).
const OFFICE_EMAIL = 'admin@excel-agri.com';

function Toggle({ id, checked, onChange, children }) {
  return (
    <div className={`haulier-selection-item ${checked ? 'selected' : ''}`}>
      <div className="checkbox-container office-recipient-row">
        <div className="check light office-recipient-check">
          <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
          <label htmlFor={id}></label>
        </div>
        <span className="haulier-name">{children}</span>
      </div>
    </div>
  );
}

function OfficeSummaryModal({ generating = false, onClose, onSend, user, warning = null }) {
  const [office, setOffice] = useState(true);
  const [self, setSelf] = useState(false);
  const [custom, setCustom] = useState(false);
  const [customEmails, setCustomEmails] = useState('');
  const [reps, setReps] = useState([]);           // [{ id, name, email, include }]
  const [repsLoading, setRepsLoading] = useState(true);
  const [officeEmail, setOfficeEmail] = useState(OFFICE_EMAIL);
  const [notice, setNotice] = useState('');
  // admin@ by default; "send as me" is the signed-in user's own address (server-resolved).
  const [sendAs, setSendAs] = useState('admin');

  useSlideOutButton([generating, custom, reps.length]);

  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/transport/planning/office-summary-recipients`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { reps: [] }))
      .then(data => {
        if (!live) return;
        setReps((data.reps || []).map(r => ({ ...r, include: r.default_selected !== false })));
        if (data.officeEmail) setOfficeEmail(data.officeEmail);
      })
      .catch(() => {})
      .finally(() => live && setRepsLoading(false));
    return () => { live = false; };
  }, []);

  const close = () => { if (!generating) onClose(); };
  const allReps = reps.length > 0 && reps.every(r => r.include);

  const send = () => {
    const list = [];
    if (office) list.push(officeEmail);
    for (const r of reps) if (r.include) list.push(r.email);
    if (self && user?.email) list.push(user.email);
    if (custom) customEmails.split(',').map(e => e.trim()).filter(Boolean).forEach(e => list.push(e));
    onSend(list, { notice, sendAs });
  };

  return createPortal(
    <div className="modal-overlay" onClick={close}>
      <div className="modal-content modal-lg office-summary" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send Transport Summary to Office</h3>
          {!generating && (
            <button className="modal-close" onClick={close}>
              <span className="material-symbols-rounded">close</span>
            </button>
          )}
        </div>
        <div className="modal-body">
          {generating ? (
            <div className="office-summary-progress">
              <div className="progress-spinner">
                <span className="material-symbols-rounded spinning-anti">sync</span>
              </div>
              <p>Generating and sending the summary...</p>
              <p className="progress-note">This may take a moment.</p>
            </div>
          ) : (
            <div className="haulier-selection-container">
              <SendWindowWarning message={warning} />
              <span>Choose who receives the full-colour transport planning summary for this day. It includes every delivery and the per-vehicle efficiency &amp; load summaries.</span>
              <div className="office-recipient-list">
                <Toggle id="office-admin" checked={office} onChange={setOffice}>{officeEmail}</Toggle>

                <div className="office-recipient-heading">
                  <span>Reps</span>
                  {reps.length > 1 && (
                    <button type="button" className="office-recipient-all"
                      onClick={() => setReps(prev => prev.map(r => ({ ...r, include: !allReps })))}>
                      {allReps ? 'Untick all' : 'Tick all'}
                    </button>
                  )}
                </div>
                {repsLoading ? (
                  <span className="office-custom-hint">Loading reps…</span>
                ) : reps.length === 0 ? (
                  <span className="office-custom-hint">No active reps have an email address.</span>
                ) : reps.map(r => (
                  <Toggle key={r.id} id={`office-rep-${r.id}`} checked={r.include}
                    onChange={on => setReps(prev => prev.map(x => (x.id === r.id ? { ...x, include: on } : x)))}>
                    {r.name} <span className="office-recipient-you">{r.email}</span>
                  </Toggle>
                ))}

                <div className="office-recipient-heading"><span>Others</span></div>
                {user?.email && (
                  <Toggle id="office-user" checked={self} onChange={setSelf}>
                    {user.email} <span className="office-recipient-you">(you)</span>
                  </Toggle>
                )}
                <Toggle id="office-custom" checked={custom} onChange={setCustom}>Custom address(es)</Toggle>
                {custom && (
                  <div className="office-custom-email">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="email1@example.com, email2@example.com"
                      value={customEmails}
                      onChange={e => setCustomEmails(e.target.value)}
                    />
                    <span className="office-custom-hint">Separate multiple addresses with commas</span>
                  </div>
                )}
              </div>
              <EmailNoticeField id="office-summary-notice" value={notice} onChange={setNotice} />
              <div className="psm-sender">
                <span className="psm-sender-label">Send from</span>
                <div className="psm-sender-select">
                  <SenderSelect user={user} value={sendAs} onChange={setSendAs} only={['admin', 'me']} />
                </div>
              </div>
            </div>
          )}
        </div>
        {!generating && (
          <div className="modal-footer">
            <button data-read-safe className="btn-round btn-danger" onClick={close}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
            <button className="btn-slideout" onClick={send}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">summarize</span></div>
              <span className="btn-label">Send Summary</span>
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default OfficeSummaryModal;
